import { z } from 'zod';

/**
 * パスワードバリデーション（superRefine）
 *
 * パスワードは任意入力（空の場合はサーバー側で更新しない）。
 * パスワードを入力した場合のみ 8文字以上・英数字混合・確認一致を検証する。
 */
const passwordRefinement = (
  data: { password?: string; passwordConfirmation?: string },
  ctx: z.RefinementCtx
): void => {
  const { password, passwordConfirmation } = data;
  if (!password) return;

  if (password.length < 8) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'パスワードは8文字以上入力してください。',
      path: ['password'],
    });
  } else if (!/[a-zA-Z]/.test(password) || !/[0-9]/.test(password)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'パスワードは英数字混合にしてください。',
      path: ['password'],
    });
  }

  if (password !== passwordConfirmation) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'パスワードとパスワード（再入力）が一致しません。',
      path: ['passwordConfirmation'],
    });
  }
};

/**
 * 利用者編集フォーム（口座連携版）の Zod スキーマを生成する
 *
 * メールアドレス必須・パスワードは任意（入力した場合のみ検証）。
 * billableFrom は通常ユーザーは必須。家族招待ユーザー（is_invitee === true）の場合は任意（PEN-992）。
 *
 * @param isInvitee - 家族招待ユーザーかどうか（convertToPresentationProps で導出）
 */
export const createEditUserFormBankAccountSchema = (isInvitee: boolean) =>
  z
    .object({
      fullName: z.string().min(1, '氏名が入力されていません。'),
      email: z
        .string()
        .min(1, 'メールアドレスが入力されていません。')
        .regex(
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          'メールアドレスの形式が正しくありません。'
        ),
      externalUserId: z.string().optional(),
      password: z.string().optional(),
      passwordConfirmation: z.string().optional(),
      // 【PEN-992 対応】家族招待ユーザー（is_invitee === true）は非課金対象のため、
      // billableFrom は null のまま保存可能。通常ユーザーは必須バリデーション（refine + transform）を適用する。
      billableFrom: isInvitee
        ? z.date({ invalid_type_error: '利用開始日を選択してください。' }).nullable()
        : z
            .date({ invalid_type_error: '利用開始日を選択してください。' })
            .nullable()
            .refine(v => v !== null, { message: '利用開始日を選択してください。' })
            .transform(v => v as Date),
    })
    .superRefine(passwordRefinement);

export type EditUserFormBankAccountFieldValues = z.infer<
  ReturnType<typeof createEditUserFormBankAccountSchema>
>;
